import {
  DocumentNode,
  GraphQLError,
  GraphQLSchema,
  TypeInfo,
  ValidationContext,
  findDeprecatedUsages,
  isExecutableDefinitionNode,
  parse,
  specifiedRules,
  visit,
  visitInParallel,
  visitWithTypeInfo,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';

import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { runOnlineParser } from '../helpers/graphqlParseHelpers';

interface ParseError {
  message: string;
  location: { line: number; column: number };
}

/**
 * Given a `schema` and a `document` (parsed query), validate the query against
 * the rules defined in `graphql`'s `specifiedRules`.
 */
function getErrors({
  schema,
  document,
}: {
  schema: GraphQLSchema;
  document: DocumentNode;
}) {
  const typeInfo = new TypeInfo(schema);
  const errors: GraphQLError[] = [];
  const context = new ValidationContext(schema, document, typeInfo, (error) => {
    // don't show a squiggle for unknown defer
    // registry schemas don't include defer and our graphql-js doesn't currently add it. Context: https://apollographql.quip.com/kwFbAO95ja7i#fIJADAGrr1j
    if (error.message !== 'Unknown directive "@defer".') {
      errors.push(error);
    }
  });

  const visitors = specifiedRules.map((rule) => rule(context));

  // Visit the whole document with each instance of all provided rules.
  visit(document, visitWithTypeInfo(typeInfo, visitInParallel(visitors)));
  return errors;
}

/**
 * Convert `GraphQLError` to a `ModelMarker`
 */
function graphQLErrorToModelMarker({
  error,
  severity,
}: {
  error: GraphQLError;
  severity: monacoEditor.MarkerSeverity;
}): monacoEditor.editor.IMarkerData | undefined {
  const node = error.nodes?.[0];
  if (!node?.loc) return;

  return {
    message: error.message,
    severity,
    startLineNumber: node.loc.startToken.line,
    startColumn: node.loc.startToken.column,
    endLineNumber: node.loc.startToken.line,
    endColumn: node.loc.startToken.column,
  };
}

/**
 * Check if operation is empty or only comments. We dont want to show an
 * error on the empty document
 */
export function isEmptyOperation(src: string) {
  return src.split('\n').every((line) => line.match(/^\s*(#.*)?$/));
}

/**
 * Given a query in text form and a schema, return all lint markers and
 * decorations to assign to the editor.
 *
 * This assumes that all deprecations are warnings and everything else is an
 * error. We can decide we want to configure what rules we check and how we
 * handle them at a later date.
 *
 * Also, this _might_ not handle any kind of highlighting besides fields. Right
 * now it just specifies the token to highlight, not actually the range.
 */
export function getLintWarnings({
  src,
  schema,
}: {
  /**
   * String representation of the query to be checked
   */
  src: string;

  /**
   * Schema to be checked against
   */
  schema: GraphQLSchema | null | undefined;
}): {
  modelMarkers: monacoEditor.editor.IMarkerData[];
  modelDecorations?: monacoEditor.editor.IModelDeltaDecoration[];
  parseErrors?: ParseError[];
} | null {
  if (!src) {
    return null;
  }
  const model = monacoEditor.editor.createModel(src);

  /**
   * Store the parsed source `DocumentNode`
   *
   * We expect invalid text to be here often, like when a user starts typing a
   * query and doesn't finish. In that case, return `null`.
   */
  let document: DocumentNode | undefined;
  let parseErrors: ParseError[] = [];
  try {
    document = parse(src);
  } catch (e) {
    const error = e as GraphQLError;
    if (isMinLengthArray(1, error.locations)) {
      parseErrors = [
        {
          message: error.message,
          location: error.locations[0],
        },
      ];
    }
  }

  if (parseErrors.length) {
    if (isEmptyOperation(src)) {
      return null;
    }

    // There are some examples of invalid syntax which will not produce an
    // 'Invalid' state node, such as empty selection sets or args. We should
    // also capture those here. TODO (jason)
    runOnlineParser(src, (state, range) => {
      if (state.kind === 'Invalid') {
        const { lineNumber, column } = range.getStartPosition();
        if (
          column !== parseErrors[0]?.location.column ||
          lineNumber !== parseErrors[0]?.location.line
        ) {
          parseErrors.push({
            message: `Syntax Error: Unexpected ${model.getValueInRange(range)}`,
            location: {
              line: lineNumber,
              column,
            },
          });
        }
      }
    });

    // A GraphQL syntax error only has a location and no node, because we don't have an AST
    // So we use the online parser to get the range of the token at that location
    return {
      modelMarkers: parseErrors.map((error) => {
        const { line, column } = error.location;
        return {
          severity: monacoEditor.MarkerSeverity.Error,
          message: error.message,
          source: 'GraphQL: Syntax',
          startLineNumber: line,
          startColumn: column,
          endLineNumber: line,
          endColumn: column,
        };
      }),
      modelDecorations: parseErrors.map((error) => {
        const { line, column } = error.location;
        return {
          range: new monacoEditor.Range(line, 0, line, column),
          options: {
            isWholeLine: true,
            glyphMarginClassName: 'xxx-monaco-syntax-error-glyph',
            glyphMarginHoverMessage: { value: error.message },
          },
        };
      }),
      parseErrors,
    };
  }

  if (!document || !schema) {
    return null;
  }

  /**
   * Given a `schema` and a `document`, calculate deprecated usages
   */
  let deprecatedUsages: readonly GraphQLError[] = [];
  try {
    const astWithExecutableDefinitions = {
      ...document,
      definitions: document.definitions.filter(isExecutableDefinitionNode),
    };

    deprecatedUsages = findDeprecatedUsages(
      schema,
      astWithExecutableDefinitions,
    );
  } catch {
    // ignore error
  }

  let errors: GraphQLError[] = [];
  try {
    errors = getErrors({ schema, document });
  } catch {
    // ignore error
  }

  const modelMarkers = [
    deprecatedUsages.map((error) =>
      graphQLErrorToModelMarker({
        error,
        severity: monacoEditor.MarkerSeverity.Warning,
      }),
    ),
    errors.map((error) => {
      const errorMarker = graphQLErrorToModelMarker({
        error,
        severity: monacoEditor.MarkerSeverity.Error,
      });

      // TODO `graphql-lodash` support was removed from explorer May 2022. This
      // looks for the unknown directive message and explain that the feature
      // has been removed. This message can eventually be removed.
      if (error.message === 'Unknown directive "@_".') {
        return {
          ...errorMarker,
          message:
            'Unknown directive "@_": Explorer no longer supports graphql-lodash usage.',
        };
      }
      return errorMarker;
    }),
  ]
    .flat()
    .filter(
      (modelMarker): modelMarker is monacoEditor.editor.IMarkerData =>
        !!modelMarker,
    );

  return modelMarkers.length ? { modelMarkers } : null;
}
